import React, { useState, useRef, useEffect } from 'react';
import { Coins, LogIn, UserPlus, LayoutDashboard, Sparkles, Layers3, Sofa, PenTool, Globe, MapPinned, Film, Zap, Settings, Users, LogOut, ChevronDown, Shield, Check, Link2, X, Gift, Handshake, LayoutGrid, Wrench, Monitor, MoonStar, Video, Clapperboard, Scissors, SlidersHorizontal, Receipt, Loader2, GraduationCap, CircleDot, FileText, FilePen } from 'lucide-react';

function formatCredits(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
    return String(n);
}
import { UserProfile } from '../types';
import { supabase, openBillingPortal } from '../services/supabase';
import { isWorldsHost } from '../utils/worldsHost';

const HyperDuskSwitcherIcon = ({ size = 18, className }: { size?: string | number; className?: string }) => (
    <MoonStar size={Number(size)} className={className} strokeWidth={1.8} />
);

// Apps grouped the way the Hub buckets them, so the switcher gives context.
// Each group carries an accent colour + gradient used to theme its section.
const APP_GROUPS = [
    {
        id: 'workspace', label: 'Workspace',
        accent: 'text-purple-300', iconBg: 'bg-purple-500/15 text-purple-300', gradient: 'from-purple-500/20',
        apps: [
            { id: 'hub', label: 'Hub', icon: LayoutDashboard, url: 'https://hub.rehub.studio' },
            { id: 'studio', label: 'Studio', icon: Clapperboard, url: 'https://studio.rehub.studio' },
        ],
    },
    {
        id: 'photo', label: 'AI Photo',
        accent: 'text-sky-300', iconBg: 'bg-sky-500/15 text-sky-300', gradient: 'from-sky-500/20',
        apps: [
            { id: 'photoclear', label: 'Photoclear', icon: Sparkles, url: 'https://photoclear.rehub.studio' },
            { id: 'hdrphoto', label: 'HDR Photo', icon: Layers3, url: 'https://hdrphoto.rehub.studio' },
            { id: 'photoedit', label: 'PhotoEdit', icon: SlidersHorizontal, url: 'https://photoedit.rehub.studio' },
            { id: 'staging', label: 'Staging', icon: Sofa, url: 'https://staging.rehub.studio' },
        ],
    },
    {
        id: 'video', label: 'AI Video',
        accent: 'text-fuchsia-300', iconBg: 'bg-fuchsia-500/15 text-fuchsia-300', gradient: 'from-fuchsia-500/20',
        apps: [
            { id: 'imagemotion', label: 'Imagemotion', icon: Film, url: 'https://imagemotion.rehub.studio' },
            { id: 'videomotion', label: 'Videomotion', icon: Video, url: 'https://videomotion.rehub.studio' },
            { id: 'propertycut', label: 'PropertyCuts', icon: Scissors, url: 'https://propertycuts.rehub.studio' },
            { id: 'hyperdusk', label: 'Hyper Dusk', icon: HyperDuskSwitcherIcon, url: 'https://hyperdusk.rehub.studio' },
            { id: 'record', label: 'Screen Record', icon: CircleDot, url: 'https://record.rehub.studio' },
        ],
    },
    {
        id: 'tours', label: 'Tours & Floor Plans',
        accent: 'text-emerald-300', iconBg: 'bg-emerald-500/15 text-emerald-300', gradient: 'from-emerald-500/20',
        apps: [
            { id: 'tours', label: '360 Tours', icon: Globe, url: 'https://360tours.rehub.studio' },
            { id: 'planos', label: 'Planos', icon: MapPinned, url: 'https://planos.rehub.studio' },
            { id: 'sketchplan', label: 'Sketchplan', icon: PenTool, url: 'https://sketchplan.rehub.studio' },
        ],
    },
    {
        id: 'present', label: 'Presentations',
        accent: 'text-amber-300', iconBg: 'bg-amber-500/15 text-amber-300', gradient: 'from-amber-500/20',
        apps: [
            { id: 'showcase', label: 'Showcase', icon: Monitor, url: 'https://showcase.rehub.studio' },
            { id: 'brochures', label: 'BrochurePDF', icon: FileText, url: 'https://brochures.rehub.studio' },
            { id: 'pdf', label: 'ReHub PDF', icon: FilePen, url: 'https://pdf.rehub.studio' },
        ],
    },
    {
        id: 'tools', label: 'Free Tools',
        accent: 'text-slate-300', iconBg: 'bg-slate-500/15 text-slate-300', gradient: 'from-slate-500/20',
        apps: [
            { id: 'tools', label: 'Tools', icon: Wrench, url: 'https://tools.rehub.studio' },
        ],
    },
] as const;

const APPS = APP_GROUPS.flatMap((g) => g.apps as readonly (typeof APP_GROUPS)[number]['apps'][number][]);

const handleAppNavigation = async (targetApp: string, url: string, currentApp: string, e: React.MouseEvent) => {
    if (targetApp === currentApp) {
        e.preventDefault();
        return;
    }
    try {
        const { supabase } = await import('../services/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token && session?.refresh_token) {
            e.preventDefault();
            window.location.href = `${url}#sso_access_token=${session.access_token}&sso_refresh_token=${session.refresh_token}`;
        }
    } catch (err) {
        console.warn('SSO navigation failed, using default navigation');
    }
};

const ProductSwitcher: React.FC<{ currentApp: string }> = ({ currentApp }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const close = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, []);

    return (
        <div className="relative hidden md:block" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800/90 border border-slate-600/50 text-slate-200 hover:text-white hover:bg-slate-700/80 transition-all text-sm font-medium"
                aria-expanded={open}
                aria-haspopup="true"
            >
                <LayoutGrid size={16} />
                <span>Apps</span>
                <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-2 w-[340px] max-h-[80vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl z-50">
                    <div className="relative overflow-hidden rounded-2xl">
                        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_0%_0%,rgba(168,85,247,0.18),transparent_55%),radial-gradient(120%_80%_at_100%_100%,rgba(56,189,248,0.12),transparent_55%)]" />
                        <div className="relative p-3 space-y-3">
                            {APP_GROUPS.map((group) => (
                                <div key={group.id}>
                                    <div className={`px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider ${group.accent}`}>{group.label}</div>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {group.apps.map((app) => (
                                            <a
                                                key={app.id}
                                                href={app.url}
                                                onClick={(e) => { handleAppNavigation(app.id, app.url, currentApp, e); setOpen(false); }}
                                                className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                                    currentApp === app.id
                                                        ? `bg-gradient-to-br ${group.gradient} to-transparent text-white ring-1 ring-inset ring-white/15`
                                                        : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
                                                }`}
                                            >
                                                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${group.iconBg}`}>
                                                    <app.icon size={16} strokeWidth={1.8} />
                                                </span>
                                                <span className="truncate">{app.label}</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const MobileAppSwitcher: React.FC<{ currentApp: string }> = ({ currentApp }) => (
    <div className="flex items-center gap-1.5" aria-label="Switch app">
        {APPS.map(app => (
            <a
                key={app.id}
                href={app.url}
                onClick={(e) => handleAppNavigation(app.id, app.url, currentApp, e)}
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all touch-manipulation ${
                    currentApp === app.id
                        ? 'bg-orange-600 border-orange-500 text-white'
                        : 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300 active:bg-slate-800'
                }`}
                aria-label={`Switch to ${app.label}`}
            >
                <app.icon size={18} strokeWidth={2} />
            </a>
        ))}
    </div>
);

// ── Header ──────────────────────────────────────────────────────
interface HeaderProps {
    user?: UserProfile | null;
    onLogin: () => void;
    onSignUp: () => void;
    onOpenCredits: () => void;
    onOpenPlans?: () => void;
    onHome: () => void;
    onNavigateToSettings?: () => void;
    onNavigateToMembers?: () => void;
    onNavigateToEducation?: () => void;
    onNavigateToReferrals?: () => void;
    isAdmin?: boolean;
    onOpenAdmin?: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, onLogin, onSignUp, onOpenCredits, onOpenPlans, onHome, onNavigateToSettings, onNavigateToMembers, onNavigateToEducation, onNavigateToReferrals, isAdmin, onOpenAdmin }) => {
    const [accountOpen, setAccountOpen] = useState(false);
    const [creditsPanelOpen, setCreditsPanelOpen] = useState(false);
    const [billingLoading, setBillingLoading] = useState(false);
    const [billingNotice, setBillingNotice] = useState<string | null>(null);
    const accountRef = useRef<HTMLDivElement>(null);
    const creditsRef = useRef<HTMLDivElement>(null);
    // On a 360Worlds consumer host (e.g. explore.360worlds.com) the shared
    // 360 Tours Studio / ReHub chrome (logo, Apps switcher, credits cross-sell,
    // referrals, Partner Programme) is off-brand and confusing, so we swap the
    // logo to 360Worlds and hide the ReHub-specific surfaces.
    const worlds = isWorldsHost();

    const handleBillingPortal = async () => {
        if (billingLoading) return;
        setBillingNotice(null);
        setBillingLoading(true);
        try {
            const result = await openBillingPortal();
            if ('noCustomer' in result) {
                setBillingNotice("No invoices yet — buy credits or a plan and they'll appear here.");
            } else {
                window.location.href = result.url;
            }
        } catch (err) {
            console.error('[Billing] portal failed:', err);
            setBillingNotice("Couldn't open billing right now. Please try again.");
        } finally {
            setBillingLoading(false);
        }
    };

    useEffect(() => {
        const close = (e: MouseEvent) => {
            if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountOpen(false);
            if (creditsRef.current && !creditsRef.current.contains(e.target as Node)) setCreditsPanelOpen(false);
        };
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, []);

    return (
        <header className="sticky top-0 left-0 right-0 bg-black border-b border-slate-900 shrink-0 z-40 flex flex-col md:flex-row md:items-center md:h-16 px-4 sm:px-6">
            <div className="flex flex-col md:flex-row md:flex-1 md:items-center md:justify-between">
                <div className="flex items-center justify-between w-full min-h-14 md:min-h-0 py-3 md:py-0 gap-3">
                    <div className="flex items-center gap-2 sm:gap-4">
                        <div
                            className="flex items-center gap-2 cursor-pointer group"
                            onClick={onHome}
                        >
                            <img
                                src={worlds ? '/360worlds-logo.png' : '/360tours-studio-logo-transparent.png'}
                                alt={worlds ? '360Worlds' : '360 Tours Studio'}
                                className="h-11 sm:h-14 w-auto max-w-[200px] sm:max-w-[260px] object-contain object-left group-hover:opacity-90 transition-all"
                            />
                        </div>

                        {!worlds && <ProductSwitcher currentApp="tours" />}
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto" ref={accountRef}>
                {user ? (
                    <>
                        {!worlds && onNavigateToReferrals && (
                            <button
                                onClick={onNavigateToReferrals}
                                className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full text-emerald-300 hover:bg-emerald-600/30 hover:text-emerald-200 transition-all text-xs font-medium min-h-[36px]"
                            >
                                <Gift size={14} />
                                <span>Give 50, Get 50</span>
                            </button>
                        )}
                        {!worlds && <div className="relative" ref={creditsRef}>
                            {user.credits === 0 ? (
                                <button
                                    onClick={() => setCreditsPanelOpen((o) => !o)}
                                    className="flex items-center gap-1.5 px-2.5 py-2 pr-3 shrink-0 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 rounded-full border-2 border-orange-400 transition-all group shadow-lg shadow-orange-500/40 ring-2 ring-orange-400/30 animate-pulse hover:animate-none min-h-[40px]"
                                >
                                    <div className="w-6 h-6 rounded-full bg-red-700 text-white flex items-center justify-center border border-red-400 group-hover:border-orange-300">
                                        <Coins size={12} />
                                    </div>
                                    <div className="flex flex-col items-start leading-none">
                                        <span className="font-bold text-white text-xs">Top Up</span>
                                        <span className="text-[10px] text-orange-100 font-medium hidden sm:inline">0 left</span>
                                    </div>
                                </button>
                            ) : (
                                <button
                                    onClick={() => setCreditsPanelOpen((o) => !o)}
                                    className="flex items-center gap-1.5 px-2.5 py-2 pr-3 shrink-0 bg-orange-600 hover:bg-orange-500 rounded-full border-2 border-orange-400 transition-all group shadow-lg shadow-orange-500/40 ring-2 ring-orange-400/30 min-h-[40px]"
                                >
                                    <div className="w-6 h-6 rounded-full bg-orange-700 text-white flex items-center justify-center border border-orange-400 group-hover:border-orange-300">
                                        <LayoutDashboard size={12} />
                                    </div>
                                    <div className="flex flex-col items-start leading-none">
                                        <div className="flex items-center gap-1">
                                            <span className="font-bold text-white text-sm sm:text-base">{formatCredits(user.credits)}</span>
                                            <Coins size={12} className="text-white" />
                                        </div>
                                        <span className="text-[10px] text-orange-100 font-semibold hidden sm:block">Credits</span>
                                    </div>
                                </button>
                            )}
                            {creditsPanelOpen && (
                                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={() => setCreditsPanelOpen(false)}>
                                    <div className="bg-slate-900 border border-slate-600 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
                                        <div className="p-6 relative">
                                            <button type="button" onClick={() => setCreditsPanelOpen(false)} className="absolute top-4 right-4 p-1.5 hover:bg-slate-800 rounded-lg transition-colors">
                                                <X size={18} className="text-slate-400" />
                                            </button>
                                            <div className="p-4 mb-4 rounded-xl border border-slate-700 bg-gradient-to-b from-orange-950/40 to-transparent">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <a href="https://hub.rehub.studio" target="_blank" rel="noopener noreferrer" className="shrink-0">
                                                        <img src="/ReHub%20Logo%20Transparent%20White%20Text%20.png" alt="ReHub Studio" className="h-12 w-auto object-contain" />
                                                    </a>
                                                    <h3 className="font-bold text-white text-base">ReHub Credits</h3>
                                                </div>
                                                <p className="text-xs font-semibold text-orange-300/90 uppercase tracking-wide mb-1">Five apps in one · One balance</p>
                                                <p className="text-slate-300 text-sm mb-3">
                                                    Your balance: <span className="font-semibold text-orange-200">{formatCredits(user.credits)}</span> · Use across PhotoClear, Staging, Sketchplan, 360 Tours & Planos
                                                </p>
                                                <p className="text-xs text-slate-400 flex items-center gap-1">
                                                    <Link2 size={12} /> Buy once, use anywhere
                                                </p>
                                            </div>
                                            <div className="p-4 space-y-4 border-b border-slate-700/80">
                                                <div className="flex gap-3 items-start">
                                                    <img src="https://360tours.rehub.studio/360tours-studio-logo-transparent.png" alt="360 Tours" className="h-14 w-auto shrink-0 object-contain" />
                                                    <div>
                                                        <p className="text-xs font-semibold text-orange-200/90 mb-0.5">360 Tours</p>
                                                        <p className="text-sm text-slate-400 leading-snug">Create, host and share immersive 360° virtual tours. Embed on any site, fast and mobile-friendly.</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-3 items-start">
                                                    <img src="https://planos.rehub.studio/Planos%20Logo%20Red.png" alt="Planos" className="h-10 w-auto shrink-0 object-contain" />
                                                    <div>
                                                        <p className="text-xs font-semibold text-red-200/90 mb-0.5">Planos</p>
                                                        <p className="text-sm text-slate-400 leading-snug">Overlay floor plans on Google Maps, add camera hotspots with real photos, and publish interactive spatial maps.</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="p-4 space-y-3">
                                                <div>
                                                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Benefits</p>
                                                    <ul className="space-y-1.5 text-sm text-slate-400">
                                                        <li className="flex items-center gap-2"><Check size={14} className="text-orange-500 shrink-0" /> ReHub Credits per month on plans</li>
                                                        <li className="flex items-center gap-2"><Check size={14} className="text-orange-500 shrink-0" /> Same balance across all five apps</li>
                                                        <li className="flex items-center gap-2"><Check size={14} className="text-orange-500 shrink-0" /> Never expire</li>
                                                    </ul>
                                                </div>
                                            </div>
                                            <div className="pt-3">
                                                <button
                                                    type="button"
                                                    onClick={() => { setCreditsPanelOpen(false); onOpenCredits(); }}
                                                    className="w-full py-2.5 rounded-lg bg-orange-600 hover:bg-orange-500 text-white font-semibold text-sm transition-colors"
                                                >
                                                    {user.credits === 0 ? 'Get credits' : 'Buy more credits'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>}
                        <div className="relative">
                            <button
                                onClick={() => setAccountOpen((o) => !o)}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium min-h-[44px]"
                                aria-expanded={accountOpen}
                                aria-haspopup="true"
                            >
                                <span className="max-w-[120px] truncate hidden sm:inline">{user.email || 'Account'}</span>
                                <ChevronDown size={16} className={`transition-transform ${accountOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {accountOpen && (
                                <div className="absolute right-0 top-full mt-1 py-1 min-w-[180px] bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50">
                                    {!worlds && onOpenPlans && (
                                        <button
                                            onClick={() => { onOpenPlans(); setAccountOpen(false); }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                                        >
                                            <Zap size={16} />
                                            Plans (active spaces)
                                        </button>
                                    )}
                                    {onNavigateToSettings && (
                                        <button
                                            onClick={() => { onNavigateToSettings(); setAccountOpen(false); }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                                        >
                                            <Settings size={16} />
                                            Settings
                                        </button>
                                    )}
                                    {!worlds && onNavigateToMembers && (
                                        <button
                                            onClick={() => { onNavigateToMembers(); setAccountOpen(false); }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                                        >
                                            <Users size={16} />
                                            Members
                                        </button>
                                    )}
                                    {onNavigateToEducation && (
                                        <button
                                            onClick={() => { onNavigateToEducation(); setAccountOpen(false); }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                                        >
                                            <GraduationCap size={16} />
                                            Teacher dashboard
                                        </button>
                                    )}
                                    {!worlds && onNavigateToReferrals && (
                                        <button
                                            onClick={() => { onNavigateToReferrals(); setAccountOpen(false); }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                                        >
                                            <Gift size={16} />
                                            Give 50, Get 50
                                        </button>
                                    )}
                                    {!worlds && (
                                        <a
                                            href="https://hub.rehub.studio/#/partner"
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                                            onClick={() => setAccountOpen(false)}
                                        >
                                            <Handshake size={16} />
                                            Partner Programme
                                        </a>
                                    )}
                                    {isAdmin && onOpenAdmin && (
                                        <button
                                            onClick={() => { onOpenAdmin(); setAccountOpen(false); }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                                        >
                                            <Shield size={16} />
                                            Admin
                                        </button>
                                    )}
                                    <button
                                        onClick={handleBillingPortal}
                                        disabled={billingLoading}
                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm disabled:opacity-60"
                                    >
                                        {billingLoading ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
                                        Billing &amp; invoices
                                    </button>
                                    {billingNotice && (
                                        <div className="px-4 py-2 text-xs text-amber-300/90">{billingNotice}</div>
                                    )}
                                    <button
                                        onClick={async () => { setAccountOpen(false); await supabase.auth.signOut(); }}
                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"
                                    >
                                        <LogOut size={16} />
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex items-center gap-1.5 sm:gap-3">
                        <button
                            onClick={onLogin}
                            className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2.5 min-h-[44px] min-w-[44px] text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl font-medium transition-all text-sm touch-manipulation"
                        >
                            <LogIn size={18} />
                            <span className="hidden sm:inline">Log In</span>
                        </button>

                        <button
                            onClick={onSignUp}
                            className="flex items-center justify-center gap-1.5 px-4 sm:px-5 py-2.5 min-h-[44px] bg-orange-600 hover:bg-orange-500 text-white rounded-xl font-semibold transition-all shadow-lg shadow-orange-500/30 text-sm touch-manipulation"
                        >
                            <UserPlus size={18} />
                            <span>Sign Up</span>
                        </button>
                    </div>
                )}
                    </div>
                </div>
            </div>

            {!worlds && (
                <div className="md:hidden border-t border-slate-800 py-2.5 flex items-center justify-center gap-3 overflow-x-auto shrink-0">
                    <MobileAppSwitcher currentApp="tours" />
                </div>
            )}
        </header>
    );
};

export default Header;
